import { useState, useEffect, useCallback } from 'react';
import { PromotionsService, VendorPromotion, AddVendorToPromotionRequest } from '../services/promotions.service';

export const usePromotedVendors = () => {
  const [promotedVendors, setPromotedVendors] = useState<VendorPromotion[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch promoted vendors
  const fetchPromotedVendors = useCallback(async (
    sortBy: 'displayOrder' | 'rating' | 'vendorCreatedAt' = 'displayOrder',
    ascending: boolean = true
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const vendors = await PromotionsService.getPromotedVendors(sortBy, ascending);
      setPromotedVendors(vendors);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch promoted vendors';
      setError(errorMessage);
      console.error('Error fetching promoted vendors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add vendor to promotions
  const addVendorToPromotion = useCallback(async (request: AddVendorToPromotionRequest) => {
    setLoading(true);
    setError(null);
    
    try {
      await PromotionsService.addVendorToPromotion(request);
      // Refetch the entire list to ensure we have complete data
      await fetchPromotedVendors();
    } catch (err: any) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to add vendor to promotions';
      setError(errorMessage);
      console.error('Error adding vendor to promotions:', err);
      throw new Error(errorMessage); // Throw with the specific message
    } finally {
      setLoading(false);
    }
  }, [fetchPromotedVendors]);

  // Remove vendor from promotions
  const removeVendorFromPromotion = useCallback(async (vendorId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      await PromotionsService.removeVendorFromPromotion(vendorId);
      setPromotedVendors(prev => prev.filter(v => v.vendorId !== vendorId));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to remove vendor from promotions';
      setError(errorMessage);
      console.error('Error removing vendor from promotions:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reorder vendors
  const reorderVendors = useCallback(async (vendorIds: string[]) => {
    setLoading(true);
    setError(null);
    
    try {
      await PromotionsService.reorderVendors(vendorIds);
      // Update local state to reflect new order
      const reordered = vendorIds.map((id, index) => {
        const vendor = promotedVendors.find(v => v.vendorId === id);
        return vendor ? { ...vendor, displayOrder: index + 1 } : null;
      }).filter(Boolean) as VendorPromotion[];
      
      setPromotedVendors(reordered);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reorder vendors';
      setError(errorMessage);
      console.error('Error reordering vendors:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [promotedVendors]);

  // Initial fetch
  useEffect(() => {
    fetchPromotedVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return {
    promotedVendors,
    loading,
    error,
    fetchPromotedVendors,
    addVendorToPromotion,
    removeVendorFromPromotion,
    reorderVendors,
  };
};

export default usePromotedVendors;
